import { useCallback, useEffect, useRef, useState } from 'react'

import { Selection } from 'prosemirror-state'
import {
  AdditionalPromptValues,
  ConfirmDetailsCampaignSchema,
  ConfirmDetailsSingleDocSchema,
  TurnIntoSchema,
  Values,
} from '../schemas/TurnInto'
import { TurnInto as TurnIntoIcon } from 'blaze-ui/icons/36'
import { Modal } from 'components/Wizards/Wizard/Modal'
import { Header } from 'components/Wizards/Wizard/Header/Header'
import { Source, getInputType } from './Source/Source'
import { SelectContentTypes } from './SelectContentType/SelectContentTypes'
import { CampaignType, ChannelType } from 'components/CampaignCreationModal/Types'
import { SourceType } from './Source/Types'
import { ConfirmDetails } from './ConfirmDetails/ConfirmDetails'
import { useGenerateCampaign, useGenerateDocs } from '../CampaignWizard/CampaignWizard'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useModals } from 'almanac-ui/Modal'
import { AiErrorModal } from 'components/AiErrorModal/AiErrorModal'
import { useCloseLeftSidebar } from 'hooks/useCloseLeftSidebar'
import { useCurrentFolderStore } from 'hooks/stores/useCurrentFolderStore'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useHistory, useRouteMatch } from 'react-router-dom'
import { DOC_PATH, FOLDER_DOC_PATH } from 'paths'
import { Formik, FormikProps } from 'formik'
import { useGenerateContent } from '../Wizard/useGenerateContent'
import { FormSchema, FormStatus } from '../Wizard/Form/Types'
import { Sections } from '../Wizard/Form/Sections'
import { Campaign } from 'almanac-ui/icons/blaze/32'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { MobileHeader } from '../Wizard/Header/MobileHeader'
import { useCreateCardDismissal } from 'hooks/queries/useCreateCardDismissal'
import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import { TextChannels } from '../../../constants'
import { TurnInto as TurnIntoType } from 'types'
import { object, string } from 'yup'
import { useBrandKits } from 'hooks/queries/brandKits/useBrandKits'
import { Tone } from 'components/DocRightRail/screens/AIAssistant/ToneSelect'
import { ensureChannelsHavePostAt } from './utils'

interface Props {
  initialValues?: Values
  sourceType?: SourceType
  initialIsCampaign?: boolean
  fromDocProps?: {
    documentAsMarkdown?: string
    selection?: Selection
  }
}

const sourceTypeToTitle = (sourceType?: SourceType) => {
  switch (sourceType) {
    case SourceType.VideoFile:
      return TextChannels[TurnIntoType.VideoIntoContent].name
    case SourceType.AudioFile:
      return TextChannels[TurnIntoType.AudioIntoContent].name
    case SourceType.File:
      return TextChannels[TurnIntoType.DocsIntoContent].name
    case SourceType.Website:
      return TextChannels[TurnIntoType.WebsitesIntoContent].name
    default:
      return 'Turn Into'
  }
}

export const AUDIO_FOCUS =
  'Write a new doc in the format and style requested, using the transcribed audio as the primary basis for your new document.'
export const CAMPAIGN_FOCUS = 'Use the following text content as source material for the content you will generate: '

export const TurnIntoWizard = ({ initialValues = {}, sourceType, fromDocProps, initialIsCampaign = false }: Props) => {
  const { mutate: dismissCard } = useCreateCardDismissal()
  const determineInitialSteps = () => {
    if (initialIsCampaign && fromDocProps) {
      return {
        currentStep: 2,
        initialSourceStep: 3,
      }
    }

    if (fromDocProps) {
      return {
        currentStep: 1,
        initialSourceStep: 3,
      }
    }

    if (sourceType) {
      return {
        currentStep: 1,
        initialSourceStep: 1,
      }
    }

    return {
      currentStep: 1,
      initialSourceStep: 0,
    }
  }

  const initialSteps = determineInitialSteps()

  const [currentStep, setCurrentStep] = useState(initialSteps.currentStep)
  const [initialSourceStep, setInitialSourceStep] = useState(initialSteps.initialSourceStep)

  const workspaceId = useCurrentWorkspaceId()
  const modalRootRef = useRef<HTMLDivElement>(null)
  const [isCampaign, setIsCampaign] = useState(initialIsCampaign)
  const formikRef = useRef<FormikProps<Values>>(null)
  const [localSourceType, setLocalSourceType] = useState(sourceType || SourceType.Website)

  const { data: brandKits } = useBrandKits()

  const getBrandKit = useCallback(
    (id?: number) => {
      if (!brandKits) return undefined
      if (id) return brandKits.find((kit) => kit.id === id)
      return brandKits[0]
    },
    [brandKits],
  )

  const initialBrandKit = getBrandKit(initialValues.brand_kit_id)
  const formInitialValues = {
    filename: initialValues?.filename || '',
    project: initialValues?.project,
    source_materials: initialValues?.source_materials || [],
    focus: ['audio', 'video'].includes(getInputType(localSourceType) ?? '') ? AUDIO_FOCUS : '',
    channels: initialValues?.channels || [],

    brand_kit_id: initialBrandKit?.id,
    language: initialValues?.language ?? initialBrandKit?.content_preferences?.language,
  }

  const { openModal, closeModal, closeAllModals } = useModals()

  const { generateContent, generateDocMutation } = useGenerateContent({
    generationType: formikRef.current?.values.channels?.[0]?.channel_type as ChannelType,
  })

  useEffect(() => {
    if (!formikRef.current || !localSourceType) return

    formikRef.current.setFieldValue(
      'focus',
      ['audio', 'video'].includes(getInputType(localSourceType) ?? '') ? AUDIO_FOCUS : '',
    )
  }, [localSourceType])

  useEffect(() => {
    const { selection, documentAsMarkdown } = fromDocProps || {}
    const selectionText = selection?.content().content.textBetween(0, selection.content().content.size)

    if (selectionText?.trim()) {
      formikRef.current?.setFieldValue('focus', selectionText)
      formikRef.current?.setFieldValue('topic', selectionText)
    } else if (documentAsMarkdown) {
      formikRef.current?.setFieldValue('focus', documentAsMarkdown)
      formikRef.current?.setFieldValue('topic', documentAsMarkdown)
    }
  }, [fromDocProps])

  const generateCampaignMutation = useGenerateCampaign()
  const generateDocsMutation = useGenerateDocs()
  const { closeLeftSidebar } = useCloseLeftSidebar()
  const { currentFolder } = useCurrentFolderStore()
  const { isMobile } = useResponsiveDesign()
  const history = useHistory()

  const onFolderDocPath = useRouteMatch(FOLDER_DOC_PATH)

  const onDocPath = useRouteMatch(DOC_PATH)

  const onDoc = onFolderDocPath?.isExact || onDocPath?.isExact

  const generateCampaign = async (values: Values & AdditionalPromptValues) => {
    generateCampaignMutation.mutate(
      {
        workspaceId,
        name: values.filename,
        brand_voice_id: values.brand_voice_id,
        brand_kit_id: values.brand_kit_id,
        brand_visual_kit_id: values.brand_visual_kit_id,
        tones: values.tone,
        project_key: values.project?.id,
        focus: values.focus,
      },
      {
        onError: (error) => {
          openModal(AiErrorModal, {
            onCancel: closeModal,
            onTryAgain: () => {
              closeModal()
              generateCampaign(values)
            },
            error,
          })
        },
        onSuccess: ({ content_campaign }) => {
          dismissCard('home_turn_into_cta')
          generateDocs(content_campaign.id, values)
        },
      },
    )
  }

  const generateDocs = (campaignId: number, values: Values & AdditionalPromptValues) => {
    const originalStartDateAt = values.project?.brand_plan_schedule?.start_date

    generateDocsMutation.mutate(
      {
        workspaceId,
        campaign_id: campaignId,
        campaign_type: CampaignType.Multichannel,
        channels: ensureChannelsHavePostAt(values.channels, originalStartDateAt) || [],
        prompt: {
          brand_voice_id: values.brand_voice_id,
          tone: values.tone,
          transcription_id: values.transcription_id,
          language: values.language,
          include_images: true,
          brand_kit_id: values.brand_kit_id,
          brand_visual_kit_id: values.brand_visual_kit_id,
        },
      },
      {
        onError: (error) => {
          openModal(AiErrorModal, {
            onCancel: closeModal,
            onTryAgain: () => {
              closeModal()
              generateDocs(campaignId, values)
            },
            error,
          })
        },
        onSuccess: ({ almanac_file }) => {
          if (isMobile) closeLeftSidebar()
          if (almanac_file.url && (almanac_file.url !== currentFolder?.url || onDoc)) history.push(almanac_file.url)
          closeAllModals()
        },
      },
    )
  }

  const confirmDetails = () => {
    setCurrentStep(3)
  }

  const goBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
      return
    }
  }

  const generateDoc = async (values: Values & AdditionalPromptValues) => {
    await generateContent({ ...values, topic: values.focus })
  }

  const handleSubmit = async (values: Values) => {
    const brandKit = getBrandKit(values.brand_kit_id)
    const brand_kit_id = brandKit?.id
    const brand_visual_kit_id = brandKit?.brand_visual_kit?.id
    const brand_voice_id = brandKit?.brand_voice?.id
    const tone = (brandKit?.brand_voice?.voice_params.tone as Tone[]) ?? []

    const additionalPromptValues = {
      brand_kit_id,
      brand_visual_kit_id,
      brand_voice_id,
      tone,
    }

    if (isCampaign) {
      generateCampaign({ ...values, ...additionalPromptValues })
    } else {
      generateDoc({ ...values, ...additionalPromptValues })
    }
  }

  const setChannelTitles = async (completedFields: { [field: string]: any }) => {
    const filename = completedFields['filename']
    if (!filename || !formikRef.current?.values.channels) return

    const updatedChannels = formikRef.current.values.channels.map((channel) => ({
      ...channel,
      title: `${channel.title} - ${filename}`,
    }))
    await formikRef.current.setFieldValue('channels', updatedChannels)
  }

  const wizardName = initialIsCampaign ? TextChannels[GenerationType.Campaign].name : sourceTypeToTitle(localSourceType)

  const chooseSchema = () => {
    const schema = isCampaign ? ConfirmDetailsCampaignSchema : ConfirmDetailsSingleDocSchema

    if (!initialValues.filename) return schema

    const noAutoFillFilename = string().default('').meta({ hide: true, autoFill: false })

    return schema.concat(object({ filename: noAutoFillFilename })) as FormSchema<GenerationType | ChannelType>
  }

  return (
    <Modal.Root wrapperRef={modalRootRef}>
      <ResponsiveDesign.Desktop>
        <Header
          steps={['Source', 'Content Types', 'Confirm Details']}
          currentStep={currentStep}
          onStepPress={setCurrentStep}
          title={wizardName}
          icon={initialIsCampaign ? Campaign : TurnIntoIcon}
          previewUrl="https://res.cloudinary.com/almanac/image/upload/v1710183790/Home%20Card%20Previews/zapgd34o7xuxuf368iqd.png"
        />
      </ResponsiveDesign.Desktop>
      <ResponsiveDesign.Mobile>
        <MobileHeader
          steps={['Source', 'Content Types', 'Confirm Details']}
          currentStep={currentStep}
          onStepPress={setCurrentStep}
          title={wizardName}
          icon={initialIsCampaign ? Campaign : TurnIntoIcon}
        />
      </ResponsiveDesign.Mobile>
      <Formik
        innerRef={formikRef}
        initialValues={formInitialValues}
        validationSchema={TurnIntoSchema}
        onSubmit={handleSubmit}
        initialStatus={FormStatus.ManuallyFilled}
      >
        <Sections.Root schema={chooseSchema()} onAutoFill={setChannelTitles}>
          {currentStep === 1 && (
            <Source
              initialStep={initialSourceStep}
              isCampaign={isCampaign}
              setIsCampaign={setIsCampaign}
              onContinue={() => setCurrentStep(2)}
              sourceType={localSourceType}
              setSourceType={setLocalSourceType}
              isFromDoc={!!fromDocProps}
              initialIsCampaign={initialIsCampaign}
            />
          )}

          {currentStep === 2 && (
            <SelectContentTypes
              goBack={() => {
                setInitialSourceStep(3)
                goBack()
              }}
              goForward={confirmDetails}
              initialIsCampaign={initialIsCampaign}
              isMultiSelect={isCampaign}
              isFromDoc={!!fromDocProps}
              topic={formikRef.current?.values.focus}
            />
          )}
          {currentStep === 3 && (
            <ConfirmDetails
              goBack={() => setCurrentStep(2)}
              isMultiSelect={isCampaign}
              isLoading={
                generateDocsMutation.isLoading || generateCampaignMutation.isLoading || generateDocMutation.isLoading
              }
            />
          )}
        </Sections.Root>
      </Formik>
    </Modal.Root>
  )
}
